// Centralized API calls for the Analytics feature.
// - apiFetch    → the standalone charting backend (/v1/chart-engine/*)
// - apiFetch('/vaarta/…') → the delegating proxy on this backend, which re-signs
//   the caller's token for the sibling app (api registry + data-studio)
import { ApiError, apiFetch } from '../../../lib/api';
import { siblingRefusalOf, type Remedy } from '../../../lib/sibling-refusal';
import type {
  AutoLayoutResult,
  AvailableChartType,
  Chart,
  ChartDataResponse,
  ComponentCode,
  Dashboard,
  IntrospectResponse,
  PublicShare,
  Share,
  SourceMeaning,
  SourceMeaningPatch,
  GlossaryTerm,
  WorkspaceSemantics,
  DashboardDataResponse,
} from './types';

const BASE = '/v1/chart-engine';

// Data Studio is a SEPARATE service on its own origin. The browser can't call it
// directly (its CORS allowlist excludes the analytics origin) and the Vaarta platform
// no longer serves `/v1/data-studio`, so every Data Studio call MUST be forwarded by
// THIS backend's `/vaarta` reverse-proxy: apiFetch targets the charting backend
// (VITE_CHART_API_BASE_URL), whose proxy routes `v1/data-studio/*` and
// `storage/tables/*` to DATA_STUDIO_API_URL. Using vaartaFetch (the general Vaarta
// base) breaks data-studio whenever that base points straight at Vaarta (e.g.
// `/vaarta-api`), which returns 404 for the moved-out data-studio routes.
function dataStudioFetch<T>(path: string, init?: Parameters<typeof apiFetch>[1]): Promise<T> {
  return apiFetch<T>(`/vaarta${path}`, init);
}

export function listAvailableTypes() {
  return apiFetch<AvailableChartType[]>(`${BASE}/available-types`);
}

export function getComponentCode(chartType: string) {
  return apiFetch<ComponentCode>(`${BASE}/component-code/${encodeURIComponent(chartType)}`);
}

export function introspectApi(apiId: string) {
  return apiFetch<IntrospectResponse>(`${BASE}/introspect`, {
    method: 'POST',
    body: JSON.stringify({ api_id: apiId }),
  });
}

// What a source MEANS — the business summary, domain, grain and the metrics and
// dimensions mapped to its real fields, each with the origin of the answer.
export function fetchSourceMeaning(apiId: string) {
  return apiFetch<SourceMeaning>(`${BASE}/sources/${encodeURIComponent(apiId)}/metadata`);
}

// Declare part of it. Only the keys sent are changed, and a declared answer is
// never overwritten by a later rebuild.
export function declareSourceMeaning(apiId: string, patch: SourceMeaningPatch) {
  return apiFetch<SourceMeaning>(`${BASE}/sources/${encodeURIComponent(apiId)}/metadata`, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  });
}

// THE WORKSPACE'S SEMANTIC LAYER, in one read: every metric with the boards that
// cite it, every dimension, the groups of definitions that compute the same
// number, and the business glossary. One endpoint on purpose — declaring a term
// means naming a target, so the targets have to arrive with the terms.
export function fetchWorkspaceSemantics() {
  return apiFetch<WorkspaceSemantics>(`${BASE}/glossary`);
}

// Declare business terms. An UPSERT: sending one term edits that term and leaves
// the rest alone, so saving one row cannot delete the others by omission.
export function declareGlossaryTerms(terms: GlossaryTerm[]) {
  return apiFetch<{ ok: boolean; written: number }>(`${BASE}/glossary`, {
    method: 'PUT',
    body: JSON.stringify({ terms }),
  });
}

export function listCharts() {
  return apiFetch<Chart[]>(`${BASE}/charts`);
}

export function getChart(id: string) {
  return apiFetch<Chart>(`${BASE}/charts/${encodeURIComponent(id)}`);
}

export function createChart(payload: Partial<Chart>) {
  return apiFetch<Chart>(`${BASE}/charts`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export function updateChart(id: string, payload: Partial<Chart>) {
  return apiFetch<Chart>(`${BASE}/charts/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  });
}

export function deleteChart(id: string) {
  return apiFetch<{ ok: boolean; id: string }>(`${BASE}/charts/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  });
}

export interface ApiSource {
  id: string;
  name: string;
  // 'api' = a registry HTTP API, 'table' = a Data Studio published table,
  // 'webhook' = a push source external systems POST rows into. Used only for a
  // small badge in the picker — never shown as a URL.
  kind?: 'api' | 'table' | 'webhook';
  method?: string;
}

// ── webhook (push) data sources ───────────────────────────────────────────────

export interface WebhookSourceInfo {
  id: string;
  api_id: string; // "webhook:<id>" — bindable as a chart data source
  name: string;
  description?: string | null;
  ingest_url: string;
  row_count: number;
  max_rows: number;
  last_received_at?: string | null;
  created_at?: string | null;
  sample_curl: string;
  rows?: Array<Record<string, unknown>>;
}

export function listWebhooks() {
  return apiFetch<WebhookSourceInfo[]>(`${BASE}/webhooks`);
}
export function createWebhook(payload: { name: string; description?: string; max_rows?: number }) {
  return apiFetch<WebhookSourceInfo>(`${BASE}/webhooks`, { method: 'POST', body: JSON.stringify(payload) });
}
export function deleteWebhook(id: string) {
  return apiFetch<{ ok: boolean }>(`${BASE}/webhooks/${encodeURIComponent(id)}`, { method: 'DELETE' });
}
export function rotateWebhookToken(id: string) {
  return apiFetch<WebhookSourceInfo>(`${BASE}/webhooks/${encodeURIComponent(id)}/rotate-token`, { method: 'POST' });
}

/** One webhook, including a sample of the rows it has buffered. */
export function getWebhook(id: string) {
  return apiFetch<WebhookSourceInfo>(`${BASE}/webhooks/${encodeURIComponent(id)}`);
}

// Registry display names frequently embed the verb/path ("GET /sentiment/v2"),
// which reads like a URL. Strip a leading HTTP verb so the picker shows a clean
// API name; keep the rest as-is (we never surface the raw URL in the picker).
function cleanApiName(raw: string): string {
  const trimmed = String(raw ?? '').trim();
  const stripped = trimmed.replace(/^(GET|POST|PUT|PATCH|DELETE|HEAD|OPTIONS)\s+/i, '').trim();
  return stripped || trimmed || 'Untitled API';
}

// One flat list of every chartable data source (no categories). Registry APIs
// plus Data Studio stored tables, merged and sorted by name.
//
// Only GET-type registry APIs are chartable — a chart reads data, it never
// mutates. Mutating verbs (POST/PUT/PATCH/DELETE) are filtered out. Data Studio
// published tables are GET reads by nature, so they are always kept.
const _VERBS = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE', 'HEAD', 'OPTIONS'];

// Resolve a registry entry's HTTP verb. The field is keyed inconsistently across
// registry versions (method / http_method / verb / nested request.method), and
// some entries only encode it in the display name ("POST /api/v1/..."). Check all
// of these so non-GET entries are reliably filtered out.
function _httpMethod(it: Record<string, unknown>): string {
  const req = (it.request ?? it.config ?? it.api) as Record<string, unknown> | undefined;
  const direct = it.requestMethod ?? it.method ?? it.http_method ?? (it as Record<string, unknown>).httpMethod
    ?? it.verb ?? it.request_method ?? req?.method ?? req?.http_method;
  let m = String(direct ?? '').trim().toUpperCase();
  if (!_VERBS.includes(m)) {
    // Fall back to a leading verb token in the name, e.g. "POST /sentiment/".
    const lead = String(it.name ?? '').trim().split(/\s+/)[0].toUpperCase();
    m = _VERBS.includes(lead) ? lead : 'GET';
  }
  return m;
}

interface RegistryPage {
  data?: { items?: Array<Record<string, unknown>>; total?: number; has_more?: boolean };
}

const REGISTRY_LIMIT = 200;
const REGISTRY_MAX_PAGES = 50; // safety cap (= up to 10k APIs)

// Through the `/vaarta` proxy on THIS backend, not the direct `/vaarta-api`
// upstream. The registry lives in Automation Studio, a separate Identity Studio
// app, and our analytics token is refused there — the proxy re-signs it for
// Automation Studio on the way through. Sending it direct is why the source
// list came back empty. Same reason dataStudioFetch routes through the proxy.
/**
 * A rejection that means "your session is over", not "this endpoint is unwell".
 *
 * The difference decides whether the picker shows what resolved or the session
 * gets renewed, and the two used to be indistinguishable: every failure became
 * `null`.
 */
function isAuthError(reason: unknown): boolean {
  return reason instanceof ApiError && (reason.status === 401 || reason.status === 403);
}

/** Swallow a failing endpoint, never a failing session. */
function rethrowAuth(reason: unknown): null {
  if (isAuthError(reason)) throw reason;
  return null;
}

function registryPage(page: number) {
  return apiFetch<RegistryPage>(`/vaarta/v1/api?limit=${REGISTRY_LIMIT}&page=${page}`);
}

function registryApiSources(items: Array<Record<string, unknown>>): ApiSource[] {
  const out: ApiSource[] = [];
  for (const it of items) {
    if (_httpMethod(it) !== 'GET') continue;
    out.push({
      id: String(it.id ?? it._id ?? ''),
      name: cleanApiName(String(it.name ?? 'Untitled API')),
      kind: 'api',
      method: 'GET',
    });
  }
  return out;
}

/**
 * Every chartable data source, in one flat list: registry GET APIs, Data Studio
 * published tables, and push webhooks.
 *
 * Only GET-type registry APIs are chartable — a chart reads data, it never
 * mutates — so mutating verbs are filtered out. Data Studio tables are GET reads
 * by nature and are always kept.
 *
 * The three families are fetched CONCURRENTLY, and the registry's pages after the
 * first are fetched concurrently too (page 1 reports `total`). It used to walk up
 * to fifty pages strictly in series, on every window focus, which on a large
 * registry made opening the app feel broken.
 */
export type SourceFamily = 'registry' | 'data-studio' | 'webhooks';

/** Which family of sources could not be read, and why. */
export interface SourceOutage {
  family: SourceFamily;
  /** The Identity Studio application that refused, when it named itself. */
  target?: string;
  /** What the far side answered. */
  status: number;
  /**
   * `delegation` — we are authenticated, and the application that owns the data
   * would not let analytics act for this person. AG-1274 answers 424 and names
   * the target. This is the one that must never be shown as "you have no data
   * sources": the sources exist and cannot be read, which is a different
   * sentence with a different fix.
   *
   * `unreachable` — anything else: down, unwell, or absent.
   */
  reason: 'delegation' | 'unreachable';
  /**
   * Whether analytics held a token minted for that application when it was
   * refused. `false` means it could not get one and sent the caller's own —
   * so the likely story is a missing cross-application permission. `true`
   * means the permission worked and the far side refused anyway, which is a
   * problem over there and often temporary.
   *
   * Without this the UI has to guess, and a guess here is confidently wrong
   * advice about somebody's sign-in.
   */
  delegated?: boolean;
  /**
   * What the person should DO, decided by the backend where the facts are.
   *
   * `delegated` alone could only separate "the grant worked and they refused
   * anyway" from "we had no token" — and the second of those covers both a
   * missing grant (an administrator's) and this browser not being signed into
   * the suite as the account that owns the data (the person's, and a ten-second
   * fix). Telling the second group to find an administrator is what this adds
   * the field to stop. Absent from an older backend, and then the banner says
   * what it always said.
   */
  remedy?: Remedy;
}

export interface SourcesResult {
  sources: ApiSource[];
  unavailable: SourceOutage[];
}

/** Index-aligned with the settled array inside `fetchApiSources`. */
const FAMILIES: SourceFamily[] = ['registry', 'data-studio', 'webhooks'];

function outageOf(family: SourceFamily, reason: unknown): SourceOutage {
  const status = reason instanceof ApiError ? reason.status : 0;
  const payload = (reason instanceof ApiError ? reason.payload : null) as
    | { target?: unknown; delegated?: unknown }
    | null;
  const target = typeof payload?.target === 'string' ? payload.target : undefined;
  const delegated = typeof payload?.delegated === 'boolean' ? payload.delegated : undefined;
  return {
    family,
    target,
    status,
    reason: status === 424 ? 'delegation' : 'unreachable',
    delegated,
    // One parser for the 424 body, shared with the dialog. The banner and the
    // dialog describing the same refusal differently is the bug they would
    // otherwise both be half of.
    remedy: siblingRefusalOf(reason)?.remedy,
  };
}

export async function fetchApiSources(): Promise<SourcesResult> {
  const registry = async (): Promise<ApiSource[]> => {
    const first = await registryPage(1);
    const items = first?.data?.items ?? [];
    const sources = registryApiSources(items);
    if (!items.length) return sources;

    const total = Number(first?.data?.total ?? 0);
    const pages = total > 0
      ? Math.min(REGISTRY_MAX_PAGES, Math.ceil(total / REGISTRY_LIMIT))
      : first?.data?.has_more === false || items.length < REGISTRY_LIMIT
        ? 1
        : REGISTRY_MAX_PAGES;
    if (pages <= 1) return sources;

    // The registry does not always report `total`; when it does not, walk pages
    // until one comes back short rather than firing fifty speculative requests.
    if (total > 0) {
      const rest = await Promise.all(
        Array.from({ length: pages - 1 }, (_, i) => registryPage(i + 2).catch(rethrowAuth)),
      );
      for (const page of rest) sources.push(...registryApiSources(page?.data?.items ?? []));
      return sources;
    }

    for (let page = 2; page <= REGISTRY_MAX_PAGES; page++) {
      const next = await registryPage(page).catch(rethrowAuth);
      const nextItems = next?.data?.items ?? [];
      sources.push(...registryApiSources(nextItems));
      if (nextItems.length < REGISTRY_LIMIT) break;
    }
    return sources;
  };

  const dataStudio = async (): Promise<ApiSource[]> => {
    // Every Data Studio table the user owns, not just the ones flagged as a
    // "published API". `/tables` is the full set (its rows are readable through
    // the proxy either way); `/published-apis` is a subset that, in practice,
    // drops tables the user plainly has — so a table shows in Data Studio but
    // not here. We list `/tables` AND union in `/published-apis` so nothing the
    // old path surfaced is lost, then dedupe by the datastudio id.
    const [all, published] = await Promise.all([
      dataStudioFetch<{ items?: Array<Record<string, unknown>> }>(
        '/v1/data-studio/tables',
      ).catch(rethrowAuth),
      dataStudioFetch<{ items?: Array<Record<string, unknown>> }>(
        '/v1/data-studio/published-apis',
      ).catch(rethrowAuth),
    ]);
    const out = new Map<string, ApiSource>();
    for (const it of [...(all?.items ?? []), ...(published?.items ?? [])]) {
      // Skip only one that is EXPLICITLY unpublished; an absent flag is not "no".
      if (it.published_as_api === false) continue;
      // Identify the table by whatever id field is present. table_key is the one
      // the backend proxy uses to read rows, so prefer it; the backend matches
      // leniently across id/_id/table_id/table_key so any of these resolves.
      const dsId = it.table_key ?? it.id ?? it.table_id ?? it._id;
      if (dsId == null) continue;
      const id = `datastudio:${String(dsId)}`;
      if (!out.has(id)) {
        out.set(id, {
          id,
          name: String(it.table_name ?? it.table_key ?? 'table'),
          kind: 'table',
        });
      }
    }
    return [...out.values()];
  };

  const webhooks = async (): Promise<ApiSource[]> =>
    (await listWebhooks()).map((w) => ({ id: w.api_id, name: w.name, kind: 'webhook' as const }));

  // One family failing must not empty the picker — show whatever resolved.
  const settled = await Promise.allSettled([registry(), dataStudio(), webhooks()]);

  // …but the SESSION failing is not one family failing, and it must not be
  // reported as an empty workspace.
  //
  // Every one of these 401s when a token expires. Discarding the rejections
  // resolved this function SUCCESSFULLY with `[]`, which React Query then cached
  // as the truth — so the Data page read "No data sources yet · Connect a
  // source" for a person with sixteen sources, and kept reading it, because a
  // cached success is never retried. That is the "0 connected sources" every
  // expiry produced, and the reason a reload was the only way back.
  //
  // Rethrowing lets `apiFetch`'s 401 path renew the session and the query retry
  // against a live token. A non-auth failure keeps today's behaviour exactly:
  // show whatever resolved.
  const authFailure = settled.find(
    (r): r is PromiseRejectedResult => r.status === 'rejected' && isAuthError(r.reason),
  );
  if (authFailure) throw authFailure.reason;

  const sources = settled.flatMap((r) => (r.status === 'fulfilled' ? r.value : []));
  const unavailable = settled.flatMap((r, i) =>
    r.status === 'rejected' ? [outageOf(FAMILIES[i], r.reason)] : [],
  );
  return { sources: sources.sort((a, b) => a.name.localeCompare(b.name)), unavailable };
}

/** The sources alone, for the callers that only ever wanted the list. */
export async function listApiSources(): Promise<ApiSource[]> {
  return (await fetchApiSources()).sources;
}

// ── inline data-source creation (Add data source popup) ──────────────────────

export interface CreateApiSourceInput {
  name: string;
  url: string;
  method?: string;
  headers?: Array<{ key: string; value: string }>;
  params?: Array<{ key: string; value: string }>;
}

// Register a GET (or other) API in the API Registry without leaving Analytics.
// collection_id "" resolves to the user's default workspace server-side.
export async function createRegistryApi(input: CreateApiSourceInput): Promise<ApiSource> {
  const res = await apiFetch<{ data?: Record<string, unknown> }>('/vaarta/v1/api', {
    method: 'POST',
    body: JSON.stringify({
      collection_id: '',
      name: input.name,
      url: input.url,
      requestMethod: (input.method || 'GET').toLowerCase(),
      protocol: 'http/https',
      status: 'publish',
      headers: (input.headers || []).filter((h) => h.key).map((h) => ({ key: h.key, value: h.value })),
      params: (input.params || []).filter((p) => p.key).map((p) => ({ key: p.key, value: p.value })),
    }),
  });
  const data = res?.data ?? {};
  return { id: String(data.id ?? ''), name: String(data.name ?? input.name) };
}

export interface DataSheetField {
  name: string;
  type: 'string' | 'number' | 'boolean' | 'date' | 'datetime' | 'json';
}

export interface CreateDataSheetInput {
  tableKey?: string;
  tableName: string;
  description?: string;
  fields: DataSheetField[];
  rows: Array<Record<string, unknown>>;
  publishedAsApi?: boolean; // default YES → instantly chartable
  apiMethod?: string;       // default GET
  apiPath?: string;
  /** Rows imported so far, out of the total — a large upload is minutes of work
   *  and the dialog used to show only a disabled button for all of it. */
  onProgress?: (done: number, total: number) => void;
}

function sanitizeKey(v: string): string {
  return String(v || '').toLowerCase().replace(/[^a-z0-9_]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 80);
}

// Data Studio normalizes every schema FIELD name to this form on save (lowercase,
// non-alphanumerics → "_"). Row data keys must match it exactly, or the published
// API serves rows whose keys don't line up with the schema → charts read nulls.
// Mirror that rule here (NOT hardcoded to any sheet — same transform Data Studio
// applies) and remap row keys to it so field name == row key for every column.
function fieldKey(v: string): string {
  return String(v || '').trim().toLowerCase().replace(/[^a-z0-9_]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 96);
}

// Create a Data Studio table (published as an API by default so it is instantly
// chartable), then bulk-insert the parsed rows. Returns a `datastudio:<id>` source.
export async function createDataSheetSource(input: CreateDataSheetInput): Promise<ApiSource> {
  const key = sanitizeKey(input.tableKey || input.tableName) || `sheet_${Date.now()}`;
  const published = input.publishedAsApi !== false; // default true
  // Normalize field names so the published API's row keys match the schema fields.
  const fields = input.fields.map((f) => ({ name: fieldKey(f.name), type: f.type, expose: true }));

  const createTable = (tableKey: string, apiPath?: string) =>
    dataStudioFetch<{ item?: { id?: string; table_name?: string } }>('/v1/data-studio/tables', {
      method: 'POST',
      body: JSON.stringify({
        table_key: tableKey,
        table_name: input.tableName || tableKey,
        description: input.description || '',
        fields,
        published_as_api: published,
        api_method: input.apiMethod || 'GET',
        ...(published && apiPath ? { api_path: apiPath } : {}),
      }),
    });

  let created: { item?: { id?: string; table_name?: string } };
  try {
    created = await createTable(key, input.apiPath);
  } catch (e) {
    // A duplicate table_key / api_path — e.g. re-uploading a same-named file —
    // makes Vaarta fail (often a 500/409). Retry once with a unique suffix on
    // both so the upload still succeeds instead of erroring out.
    const status = e instanceof ApiError ? e.status : 0;
    if (status && status !== 409 && status < 500) throw e; // not a collision-type error
    const suffix = Date.now().toString(36);
    created = await createTable(`${key}_${suffix}`, input.apiPath ? `${input.apiPath}_${suffix}` : undefined);
  }
  const tableId = String(created?.item?.id ?? '');
  if (!tableId) throw new Error('Table was not created');
  if (input.rows.length) {
    const rows = input.rows.map((r) => {
      const out: Record<string, unknown> = {};
      for (const [k, v] of Object.entries(r)) out[fieldKey(k)] = v;
      return out;
    });
    // The data-studio API only exposes a SINGLE-row insert
    // (`POST .../rows` with a `{ data: {...} }` body) — there is no bulk
    // endpoint. Sending all rows in one request also tripped nginx's ~1 MB
    // body cap (413). So insert rows one-by-one, with bounded concurrency to
    // keep large uploads reasonably fast without flooding the backend.
    const url = `/v1/data-studio/tables/${encodeURIComponent(tableId)}/rows`;
    // The window is ADAPTIVE, not a fixed 8.
    //
    // Per-row insert cost rises as the table grows: measured on dev, an empty
    // table took ~21 rows/s at a fixed window of 8 and the same table at 14.5k
    // rows took ~2.6 rows/s, so a 29,639-row upload was still running after 25
    // minutes and never finished (AG-2030). A fixed window cannot answer to
    // that; it just holds the same eight requests open for longer and longer.
    //
    // So: grow the window while rows keep landing, back off on failure. The
    // ceiling is what the backend can be asked to hold at once, not a number
    // chosen for a small file.
    const MIN_INFLIGHT = 8;
    const MAX_INFLIGHT = 64;
    let inflightCap = 16;
    let live = 0;
    let next = 0;
    let failed = 0;
    let done = 0;
    let sinceAdjust = 0;

    const sendOne = async () => {
      const row = rows[next++];
      live += 1;
      try {
        await dataStudioFetch(url, { method: 'POST', body: JSON.stringify({ data: row }) });
        // A clean run of completions is the only evidence the backend can take
        // more; widen gently and only on that evidence.
        if (++sinceAdjust >= 64 && inflightCap < MAX_INFLIGHT) {
          inflightCap = Math.min(MAX_INFLIGHT, inflightCap + 8);
          sinceAdjust = 0;
        }
      } catch {
        failed += 1;
        // Back off hard on a failure — a saturated backend answers with errors
        // long before it answers slowly, and pushing harder makes both worse.
        inflightCap = Math.max(MIN_INFLIGHT, Math.floor(inflightCap / 2));
        sinceAdjust = 0;
      } finally {
        live -= 1;
        done += 1;
        input.onProgress?.(done, rows.length);
      }
    };

    const pending = new Set<Promise<void>>();
    while (next < rows.length || pending.size) {
      while (next < rows.length && live < inflightCap) {
        const p = sendOne().finally(() => pending.delete(p));
        pending.add(p);
      }
      if (pending.size) await Promise.race(pending);
    }
    if (failed) {
      // Name the number that landed. A bare "some rows failed" leaves the user
      // holding a table they cannot reason about — and because rows go in file
      // order, a partial import silently truncates the TAIL of the data.
      throw new Error(
        `Saved the table, but ${failed} of ${rows.length} rows did not import. `
        + `${rows.length - failed} rows landed, in file order — the end of the file is what is missing.`,
      );
    }
  }
  return { id: `datastudio:${tableId}`, name: input.tableName || key };
}

/**
 * Remove a connected source.
 *
 * Every other thing a workspace accumulates — charts, dashboards, webhooks,
 * shares — could be deleted, and a SOURCE could not (AG-2037). That is not a
 * tidiness problem: a failed import leaves a table holding a truncated slice of
 * the data, and an undeletable one keeps offering itself to the build agent as
 * a plausible source for boards that will then be quietly wrong.
 *
 * A Data Studio table is addressed by the id inside its `datastudio:<id>`
 * handle; a registry API by its own id. The vaarta proxy forwards DELETE
 * verbatim, so neither needs a new backend route.
 */
export function deleteSource(sourceId: string) {
  const id = String(sourceId || '');
  const table = id.startsWith('datastudio:') ? id.slice('datastudio:'.length) : '';
  return table
    ? dataStudioFetch<{ ok?: boolean }>(`/v1/data-studio/tables/${encodeURIComponent(table)}`, { method: 'DELETE' })
    : dataStudioFetch<{ ok?: boolean }>(`/v1/api/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

export function fetchChartData(
  chartId: string,
  filterContext: Record<string, unknown> = {},
  filters: Array<{ field: string; op: string; value: unknown }> = [],
  // Take the DECLARED drill step this tile offered, naming the dimension id from
  // the previous response's `drill.to`. The server validates it against the
  // hierarchy, so it cannot rebind the tile to an arbitrary column.
  drillTo?: string,
) {
  return apiFetch<ChartDataResponse>(`${BASE}/data`, {
    method: 'POST',
    body: JSON.stringify({
      chart_id: chartId, filter_context: filterContext, filters,
      ...(drillTo ? { drill_to: drillTo } : {}),
    }),
  });
}

// Every tile of a board in one response — each source read once (P2-4). A tile
// that failed is `{error}` under its id; the board never fails as a whole.
export function fetchDashboardData(
  dashboardId: string,
  filterContext: Record<string, unknown> = {},
  filters: Array<{ field: string; op: string; value: unknown }> = [],
) {
  return apiFetch<DashboardDataResponse>(`${BASE}/dashboards/${encodeURIComponent(dashboardId)}/data`, {
    method: 'POST',
    body: JSON.stringify({ filter_context: filterContext, filters }),
  });
}

// Live data for an UNSAVED chart spec — powers the builder preview with real data.
export function previewChartData(spec: Record<string, unknown>) {
  return apiFetch<ChartDataResponse>(`${BASE}/preview`, {
    method: 'POST',
    body: JSON.stringify(spec),
  });
}

// dashboards
export function listDashboards() {
  return apiFetch<Dashboard[]>(`${BASE}/dashboards`);
}
export function getDashboard(id: string) {
  return apiFetch<Dashboard>(`${BASE}/dashboards/${encodeURIComponent(id)}`);
}
export function createDashboard(payload: Partial<Dashboard>) {
  return apiFetch<Dashboard>(`${BASE}/dashboards`, { method: 'POST', body: JSON.stringify(payload) });
}
export function updateDashboard(id: string, payload: Partial<Dashboard>) {
  return apiFetch<Dashboard>(`${BASE}/dashboards/${encodeURIComponent(id)}`, { method: 'PATCH', body: JSON.stringify(payload) });
}
export function deleteDashboard(id: string) {
  return apiFetch<{ ok: boolean }>(`${BASE}/dashboards/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

/**
 * Restyle a saved dashboard from a free-form instruction ("make it light",
 * "neon dark", "match our brand").
 *
 * This endpoint existed server-side and no client called it. A raw PATCH of
 * `dashboard.theme` leaves every tile chart on its old colours; this regenerates
 * the theme AND restyles the tiles, which is why re-theming has to go through
 * here. `design_fallback` on the response means the design model was unavailable
 * and a heuristic ran instead — worth telling the user.
 */
export function applyDashboardDesign(
  id: string,
  payload: { instruction: string; logo?: string },
) {
  return apiFetch<Dashboard & { design_fallback?: boolean; pages_dropped?: boolean }>(
    `${BASE}/dashboards/${encodeURIComponent(id)}/design`,
    { method: 'POST', body: JSON.stringify(payload) },
  );
}

/** Promote a DRAFT dashboard (chatbot-built, not yet kept) to a saved one. */
export function saveDashboardDraft(id: string, name?: string) {
  return apiFetch<Dashboard>(`${BASE}/dashboards/${encodeURIComponent(id)}/save`, {
    method: 'POST',
    body: JSON.stringify(name ? { name } : {}),
  });
}

// LLM-assisted dashboard auto-layout + global-filter suggestion for a set of charts.
export function autoLayoutDashboard(chartIds: string[]) {
  return apiFetch<AutoLayoutResult>(`${BASE}/dashboards/auto-layout`, {
    method: 'POST',
    body: JSON.stringify({ chart_ids: chartIds }),
  });
}

// ── sharing ──────────────────────────────────────────────────────────────────
// Create (or refresh) a public share for a chart/dashboard. Snapshot built server-side.
export function createShare(
  kind: 'chart' | 'dashboard',
  resourceId: string,
  opts?: { mode?: 'snapshot' | 'live'; refreshIntervalSeconds?: number },
) {
  return apiFetch<Share>(`${BASE}/shares`, {
    method: 'POST',
    body: JSON.stringify({
      kind,
      resource_id: resourceId,
      mode: opts?.mode ?? 'snapshot',
      refresh_interval_seconds: opts?.refreshIntervalSeconds,
    }),
  });
}
export function refreshShare(id: string) {
  return apiFetch<Share>(`${BASE}/shares/${encodeURIComponent(id)}/refresh`, { method: 'POST' });
}
export function listShares(resourceId: string) {
  return apiFetch<Share[]>(`${BASE}/shares?resource_id=${encodeURIComponent(resourceId)}`);
}
export function deleteShare(id: string) {
  return apiFetch<{ ok: boolean }>(`${BASE}/shares/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

// Public, no-auth read of a shared snapshot by token (used by the /share page).
export function getPublicShare(token: string) {
  return apiFetch<PublicShare>(`/v1/public/share/${encodeURIComponent(token)}`, { authenticated: false });
}
